import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'

import { translateError, translateIssue } from '@gx-cms/error-messages'
import {
  HOST_SOURCE,
  PLUGIN_SOURCE,
  PROTOCOL_VERSION,
  requiredCapability,
} from '@plugin-protocol'
import type {
  ApiDownloadParams,
  ApiRequestParams,
  ApiUploadParams,
  HostContext,
  HostMessage,
  PluginMessage,
} from '@plugin-protocol/types'

import { useAuthStore } from '../../features/auth/hooks'
import { MediaPicker } from '../../features/media'
import type { MediaAsset } from '../../shared/api'
import { api, ApiError, download, upload } from '../../shared/api'
import { confirm, Icon } from '../../shared/components'
import { resolveTheme, toast, useUiStore } from '../../shared/hooks'
import { useT } from '../../shared/i18n'
import { pluginIcon } from '../../shared/icons'
import { useAdminExtensions, useLocales } from '../hooks'
import { frameUrl, pluginLocale, pluginOf } from '../registry'

import type { PluginFrameProps } from './PluginFrame.types'

/**
 * Host container for plugin UI (extensions contract v1). The frame runs in a
 * sandbox with an opaque origin and no network access; this component is the
 * only bridge. Every API request is checked against the route allowlist and
 * the plugin's granted capabilities, then executed with the signed-in user's
 * session — so a plugin can never exceed either.
 */
export function PluginFrame(props: PluginFrameProps) {
  const {
    pluginKey,
    adminUrl,
    kind,
    extensionId,
    capabilities,
    field,
    value,
    onValueChange,
    entry,
    route,
    chrome = true,
  } = props

  const frame = useRef<HTMLIFrameElement>(null)

  const [height, setHeight] = useState(
    props.initialHeight ??
      (kind === 'page' ? 640 : kind === 'dashboard' ? 240 : 160),
  )

  const [picker, setPicker] = useState<{
    multiple?: boolean
    mimePrefix?: string
    resolve: (a: MediaAsset[]) => void
  } | null>(null)

  const user = useAuthStore((s) => s.user)
  const permissions = useAuthStore((s) => s.permissions)
  const themePref = useUiStore((s) => s.theme)
  const theme = resolveTheme(themePref)
  const design = useUiStore((s) => s.design)
  const locales = useLocales()
  const { t, locale: uiLocale } = useT()
  const extensions = useAdminExtensions()
  const pluginLocales = pluginOf(extensions, pluginKey)?.locales ?? ['en']
  // The plugin UI language: the admin's when the plugin supports it, else its default (ADR-044).
  const locale = pluginLocale({ locales: pluginLocales }, uiLocale)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const lastSentValue = useRef<unknown>(value)
  const ready = useRef(false)

  const post = useCallback((msg: HostMessage) => {
    frame.current?.contentWindow?.postMessage(msg, '*')
  }, [])

  const context = useCallback(
    (): HostContext => ({
      protocolVersion: PROTOCOL_VERSION,
      pluginKey,
      extension: { kind, id: extensionId },
      theme,
      design,
      user: {
        id: user?.id ?? '',
        displayName: user?.displayName ?? '',
        permissions: [...permissions],
      },
      capabilities,
      // Default content language first (ADR-046: plugin records store it in their own columns).
      contentLocales: [
        locales.defaultLocale,
        ...locales.locales.filter((l) => l !== locales.defaultLocale),
      ],
      locale,
      uiLocale,
      pluginLocales,
      apiBaseUrl: window.location.origin,
      field,
      value,
      entry,
      route,
    }),
    [
      pluginKey,
      kind,
      extensionId,
      theme,
      design,
      user,
      permissions,
      capabilities,
      locales.locales,
      locale,
      uiLocale,
      pluginLocales,
      field,
      value,
      entry,
      route,
    ],
  )

  const handleRequest = useCallback(
    async (method: string, params: unknown): Promise<unknown> => {
      const p = (params ?? {}) as Record<string, unknown>

      /** The API path a frame may call: its own service API, or a route its capabilities allow. */
      const authorize = (reqMethod: string, reqPath: string): string => {
        const clean = String(reqPath ?? '').split('?')[0]

        const own =
          !clean.includes('..') &&
          (props.apiPrefixes ?? []).some(
            (pre) => clean === pre || clean.startsWith(`${pre}/`),
          )

        // A service-backed plugin may call its own API; the service still checks the user's permissions.
        // The query string is kept (e.g. upload metadata); the prefix check uses the bare path.
        if (own) return String(reqPath)
        const cap = requiredCapability(reqMethod, reqPath)

        if (!cap)
          throw new ApiError(
            403,
            'FORBIDDEN',
            `${reqMethod} ${reqPath} is not available to plugins.`,
          )
        if (!capabilities.includes(cap))
          throw new ApiError(
            403,
            'FORBIDDEN',
            `This plugin has not been granted '${cap}'.`,
          )

        return reqPath.startsWith('/plugins/self/')
          ? reqPath.replace(
              '/plugins/self/',
              `/plugins/${encodeURIComponent(pluginKey)}/`,
            )
          : reqPath
      }

      switch (method) {
        case 'api.request': {
          const req = p as unknown as ApiRequestParams

          return api(authorize(req.method, req.path), {
            method: req.method,
            query: req.query,
            body: req.method === 'GET' ? undefined : req.body,
          })
        }

        // SDK 1.5: the frame hands over a File; the host uploads it with the user's session.
        case 'api.upload': {
          const req = p as unknown as ApiUploadParams

          if (!(req.file instanceof Blob))
            throw new ApiError(400, 'BAD_REQUEST', 'A file is required.')
          const form = new FormData()

          for (const [k, v] of Object.entries(req.fields ?? {}))
            form.append(k, String(v))
          form.append('file', req.file, String(req.fileName ?? 'file'))

          return upload(authorize('POST', req.path), form)
        }

        // SDK 1.5: the host fetches the file and saves it (the frame cannot download with credentials).
        case 'api.download': {
          const req = p as unknown as ApiDownloadParams
          const method = req.method === 'POST' ? 'POST' : 'GET'

          return download(authorize(method, req.path), {
            method,
            query: req.query,
            body: method === 'POST' ? req.body : undefined,
            fileName: req.fileName,
          })
        }

        case 'field.setValue': {
          if (kind !== 'field-widget' || entry?.readOnly)
            throw new ApiError(403, 'FORBIDDEN', t.plugins.frameNoValues)
          lastSentValue.current = p.value
          onValueChange?.(p.value)

          return null
        }

        case 'ui.toast': {
          const message = String(p.message ?? '').slice(0, 300)

          if (p.kind === 'error') toast.error({ message })
          else if (p.kind === 'success') toast.success(message)
          else toast.info(message)

          return null
        }

        case 'ui.navigate': {
          const path = String(p.path ?? '')

          if (!path.startsWith('/') || path.startsWith('//'))
            throw new ApiError(
              400,
              'VALIDATION_FAILED',
              t.plugins.frameAdminPaths,
            )
          navigate(path)

          return null
        }

        // SDK 1.7: the plugin changed its own feature flags; menus and pages follow.
        case 'ui.refreshNavigation':
          await queryClient.invalidateQueries({
            queryKey: ['admin-extensions'],
          })

          return null

        case 'ui.resize': {
          const h = Math.max(60, Math.min(4000, Number(p.height) || 0))

          setHeight(h)

          return null
        }

        case 'ui.confirm':
          return confirm({
            title: props.pluginName ?? t.plugins.plugin,
            message: String(p.message ?? '').slice(0, 500),
            confirmLabel: t.plugins.continue,
          })

        case 'ui.pickMedia': {
          if (!capabilities.includes('media.asset.read'))
            throw new ApiError(403, 'FORBIDDEN', t.plugins.frameNoMedia)
          const picked = await new Promise<MediaAsset[]>((resolve) =>
            setPicker({
              multiple: !!p.multiple,
              mimePrefix: p.mimePrefix as string | undefined,
              resolve,
            }),
          )

          return picked.map((a) => ({
            id: a.id,
            url: a.url,
            mimeType: a.mimeType,
            originalFileName: a.originalFileName,
            altText: a.altText,
            width: a.width,
            height: a.height,
          }))
        }

        default:
          throw new ApiError(
            400,
            'VALIDATION_FAILED',
            `Unknown request '${method}'.`,
          )
      }
    },
    [
      capabilities,
      entry?.readOnly,
      kind,
      navigate,
      onValueChange,
      pluginKey,
      props.pluginName,
      props.apiPrefixes,
      queryClient,
    ],
  )

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!frame.current || event.source !== frame.current.contentWindow) return
      const msg = event.data as PluginMessage

      if (!msg || msg.source !== PLUGIN_SOURCE) return

      if (msg.type === 'hello') {
        ready.current = true
        post({ source: HOST_SOURCE, type: 'init', context: context() })

        return
      }

      if (msg.type === 'request' && typeof msg.id === 'string') {
        handleRequest(msg.method, msg.params).then(
          (result) =>
            post({
              source: HOST_SOURCE,
              type: 'response',
              id: msg.id,
              ok: true,
              result,
            }),
          (err: unknown) => {
            const e =
              err instanceof ApiError
                ? err
                : new ApiError(
                    500,
                    'ERROR',
                    (err as Error)?.message ?? t.plugins.requestFailed,
                  )

            post({
              source: HOST_SOURCE,
              type: 'response',
              id: msg.id,
              ok: false,
              // In the plugin's language (ADR-044/045), with the key for plugins that translate themselves.
              error: {
                code: e.code,
                message: e.messageKey
                  ? translateError(locale, {
                      message: e.englishMessage ?? e.message,
                      messageKey: e.messageKey,
                      params: e.params,
                    })
                  : e.message,
                messageKey: e.messageKey,
                params: e.params,
                details: e.details.map((d) =>
                  d.code ? { ...d, message: translateIssue(locale, d) } : d,
                ),
              },
            })
          },
        )
      }
    }

    window.addEventListener('message', onMessage)

    return () => window.removeEventListener('message', onMessage)
  }, [context, handleRequest, post])

  useEffect(() => {
    if (ready.current)
      post({
        source: HOST_SOURCE,
        type: 'event',
        name: 'theme',
        payload: theme,
      })
  }, [theme, post])

  // Visual theme switches reach the frame (SDK 1.6, ADR-056).
  useEffect(() => {
    if (ready.current)
      post({
        source: HOST_SOURCE,
        type: 'event',
        name: 'design',
        payload: design,
      })
  }, [design, post])

  // Admin language switches reach the frame (SDK 1.4, ADR-044).
  const lastLocale = useRef(`${locale}|${uiLocale}`)

  useEffect(() => {
    const key = `${locale}|${uiLocale}`

    if (ready.current && key !== lastLocale.current) {
      lastLocale.current = key
      post({
        source: HOST_SOURCE,
        type: 'event',
        name: 'locale',
        payload: { locale, uiLocale },
      })
    }
  }, [locale, uiLocale, post])

  // Page sub-route changes (back/forward, ui.navigate within the page) reach the frame (SDK 1.2).
  const lastRoute = useRef(route)

  useEffect(() => {
    if (ready.current && route !== lastRoute.current) {
      lastRoute.current = route
      post({
        source: HOST_SOURCE,
        type: 'event',
        name: 'route',
        payload: route ?? '',
      })
    }
  }, [route, post])

  useEffect(() => {
    if (ready.current && value !== lastSentValue.current) {
      lastSentValue.current = value
      post({
        source: HOST_SOURCE,
        type: 'event',
        name: 'value',
        payload: value,
      })
    }
  }, [value, post])

  const iframe = (
    <iframe
      ref={frame}
      className="plugin-frame"
      title={props.title ?? `${pluginKey} ${extensionId}`}
      src={frameUrl(adminUrl, kind, extensionId)}
      sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
      referrerPolicy="no-referrer"
      style={{ height }}
    />
  )

  return (
    <>
      {chrome ? (
        <div className="plugin-frame-wrap">
          <div className="plugin-frame__bar">
            <span className="row" style={{ gap: 8 }}>
              <Icon icon={pluginIcon(props.icon)} />
              {props.title}
            </span>
            <span title={t.plugins.isolated}>
              {props.pluginName ?? pluginKey}
            </span>
          </div>
          {iframe}
        </div>
      ) : (
        iframe
      )}
      {picker && (
        <MediaPicker
          multiple={picker.multiple}
          mimePrefix={picker.mimePrefix}
          onClose={() => {
            picker.resolve([])
            setPicker(null)
          }}
          onPick={(assets) => {
            picker.resolve(assets)
            setPicker(null)
          }}
        />
      )}
    </>
  )
}
